import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart2,
  Hospital,
  Users,
  Settings,
  Hotel,
  Plus,
  Trash2,
  Calendar,
  AlertTriangle,
  GitBranch,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura } from "../../../shared/utils/dataNaoFutura";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";
import EquipeCirurgica from "./EquipeCirurgica";
import DadosComplementares from "./DadosComplementares";
import TecnicaOperatoria from "./TecnicaOperatoria";
import PosOperatorio from "./PosOperatorio";

// Tipo de cada item do array transplantes (extraído de Paciente)
type TransplanteItem = NonNullable<Paciente["transplantes"]>[number];

// Normalizar data para ISO (AAAA-MM-DD) para compatibilidade com input type="date"
const normalizarData = (data: string | undefined | null): string => {
  if (!data) return "";
  if (data.includes("-") && data.split("-")[0].length === 4) return data;
  if (data.includes("/")) {
    const partes = data.split("/");
    if (partes.length === 3 && partes[2].length === 4) {
      return `${partes[2]}-${partes[1]}-${partes[0]}`;
    }
  }
  return data;
};

interface DadosTransplanteProps {
  paciente: Paciente;
}

export default function DadosTransplante({ paciente }: DadosTransplanteProps) {
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): dados do transplante sao campos CLINICOS do doc
  // pacientes - edicao restrita a equipe clinica (espelho das
  // firestore.rules). Nome legado: hoje significa "somente leitura"
  // (vale p/ convidado E administrativo).
  const isConvidado =
    !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado);
  const [subAba, setSubAba] = useState<
    "equipe" | "dados" | "tecnica" | "pos" | "timeline"
  >("equipe");
  const [txSelecionado, setTxSelecionado] = useState(0);
  const [editandoHeader, setEditandoHeader] = useState(false);
  const [dataTemp, setDataTemp] = useState({ dia: "", mes: "", ano: "" });

  const transplantes: TransplanteItem[] = useMemo(() => {
    if (paciente.transplantes && paciente.transplantes.length > 0)
      return paciente.transplantes;
    if (paciente.dadosTransplante) {
      const temDados = Object.values(paciente.dadosTransplante).some(
        (v) => v && typeof v === "object" && Object.keys(v).length > 0
      );
      if (temDados)
        return [
          {
            ordem: 1,
            dataTransplante: normalizarData(
              paciente.inscricaoTx?.dataTransplante
            ),
            equipeCirurgica: paciente.dadosTransplante.equipeCirurgica,
            dadosComplementares: paciente.dadosTransplante.dadosComplementares,
            tecnicaOperatoria: paciente.dadosTransplante.tecnicaOperatoria,
            posOperatorio: paciente.dadosTransplante.posOperatorio,
          },
        ];
    }
    return [
      {
        ordem: 1,
        dataTransplante: normalizarData(paciente.inscricaoTx?.dataTransplante),
      },
    ];
  }, [
    paciente.transplantes,
    paciente.dadosTransplante,
    paciente.inscricaoTx?.dataTransplante,
  ]);

  useEffect(() => {
    if (txSelecionado >= transplantes.length)
      setTxSelecionado(transplantes.length - 1);
  }, [transplantes.length, txSelecionado]);

  const txAtual: TransplanteItem =
    transplantes[txSelecionado] || transplantes[0];
  // Janela de 24h para edição do header (data/motivo) do ReTx
  const headerEditadoEm = txAtual?.headerEditadoEm;
  const horasDesdeEdicaoHeader = headerEditadoEm
    ? (new Date().getTime() - new Date(headerEditadoEm).getTime()) /
      (1000 * 60 * 60)
    : 0;
  const headerDentroJanela24h = !headerEditadoEm || horasDesdeEdicaoHeader < 24;

  // ============================================
  // SALVAR DADOS DE UMA SEÇÃO DO TX
  // ============================================
  const salvarDadosTx = (
    secao:
      | "equipeCirurgica"
      | "dadosComplementares"
      | "tecnicaOperatoria"
      | "posOperatorio",
    dados: any
  ) => {
    const novos: TransplanteItem[] = [...transplantes];
    novos[txSelecionado] = { ...novos[txSelecionado], [secao]: dados };
    const ultimo = novos[novos.length - 1];
    atualizarPaciente(paciente.id, {
      transplantes: novos,
      dadosTransplante: {
        equipeCirurgica: ultimo.equipeCirurgica ?? {},
        dadosComplementares: ultimo.dadosComplementares ?? {},
        tecnicaOperatoria: ultimo.tecnicaOperatoria ?? {},
        posOperatorio: ultimo.posOperatorio ?? {},
      },
    });
  };

  // ============================================
  // SALVAR DATA E MOTIVO DO TX (header editável)
  // ============================================
  const salvarHeaderTx = (
    campo: "dataTransplante" | "motivoRetx",
    valor: string
  ) => {
    // 📅 B296 — data do transplante não pode ser futura (permite hoje/retroativo).
    if (campo === "dataTransplante" && ehDataFutura(valor)) {
      alert(msgDataFutura("Data do transplante"));
      return;
    }
    const novos: TransplanteItem[] = [...transplantes];
    novos[txSelecionado] = {
      ...novos[txSelecionado],
      [campo]: valor,
      headerEditadoEm: new Date().toISOString(),
    };
    atualizarPaciente(paciente.id, { transplantes: novos });
  };

  // ============================================
  // ADICIONAR NOVO TX (com confirmação)
  // ============================================
  const adicionarTransplante = () => {
    const confirmado = window.confirm(
      `Confirma o registro de um ${
        transplantes.length + 1
      }º transplante (ReTx) para este paciente?\n\nEsta ação criará um novo registro de transplante.`
    );
    if (!confirmado) return;

    const novos: TransplanteItem[] = [
      ...transplantes,
      { ordem: transplantes.length + 1, dataTransplante: "" },
    ];
    atualizarPaciente(paciente.id, { transplantes: novos });
    setTxSelecionado(novos.length - 1);
    setSubAba("equipe");
    setDataTemp({ dia: "", mes: "", ano: "" });
    setEditandoHeader(true);
  };

  // ============================================
  // REMOVER TX VAZIO (só se não tem dados preenchidos)
  // ============================================
  const removerTransplante = () => {
    if (transplantes.length <= 1) return;
    const tx = transplantes[txSelecionado];
    const temDadosPreenchidos =
      tx.equipeCirurgica &&
      Object.values(tx.equipeCirurgica).some(
        (v) => v && String(v).trim() !== ""
      );
    const temDadosComp =
      tx.dadosComplementares &&
      Object.values(tx.dadosComplementares).some(
        (v) => v && String(v).trim() !== ""
      );
    const temTecnica =
      tx.tecnicaOperatoria &&
      Object.values(tx.tecnicaOperatoria).some(
        (v) => v && String(v).trim() !== ""
      );
    const temPos =
      tx.posOperatorio &&
      Object.values(tx.posOperatorio).some((v) => v && String(v).trim() !== "");

    if (temDadosPreenchidos || temDadosComp || temTecnica || temPos) {
      alert(
        "Este transplante já possui dados preenchidos e não pode ser removido.\n\nPara manter a integridade do prontuário, transplantes com dados só podem ser editados."
      );
      return;
    }

    const confirmado = window.confirm(
      `Remover o ${tx.ordem}º transplante?\n\nEste registro está vazio e será excluído permanentemente.`
    );
    if (!confirmado) return;

    const novos = transplantes
      .filter((_, idx) => idx !== txSelecionado)
      .map((t, idx) => ({ ...t, ordem: idx + 1 }));

    const ultimo = novos[novos.length - 1];
    atualizarPaciente(paciente.id, {
      transplantes: novos,
      dadosTransplante: {
        equipeCirurgica: ultimo.equipeCirurgica,
        dadosComplementares: ultimo.dadosComplementares,
        tecnicaOperatoria: ultimo.tecnicaOperatoria,
        posOperatorio: ultimo.posOperatorio,
      },
    });
    setTxSelecionado(Math.max(0, txSelecionado - 1));
    setEditandoHeader(false);
  };

  // Paciente virtual: injeta dadosTransplante do Tx selecionado
  const pacienteVirtual: Paciente = {
    ...paciente,
    dadosTransplante: {
      equipeCirurgica: txAtual.equipeCirurgica,
      dadosComplementares: txAtual.dadosComplementares,
      tecnicaOperatoria: txAtual.tecnicaOperatoria,
      posOperatorio: txAtual.posOperatorio,
    },
  };

  const subAbas: { id: string; label: string; icon?: React.ElementType }[] = [
    { id: "equipe", label: "Equipe Cirúrgica", icon: Users },
    { id: "dados", label: "Dados Complementares", icon: BarChart2 },
    { id: "tecnica", label: "Técnica Operatória", icon: Settings },
    { id: "pos", label: "Pós-Operatório", icon: Hotel },
    ...(transplantes.length > 1
      ? [{ id: "timeline", label: "Timeline", icon: GitBranch }]
      : []),
  ];

  // Helper: verificar preenchimento de uma seção
  const verificarPreenchimento = (secao: any): boolean => {
    if (!secao || typeof secao !== "object") return false;
    return Object.entries(secao).some(
      ([k, v]) => k !== "editadoEm" && v && String(v).trim() !== ""
    );
  };

  // ============================================
  // FORMATAÇÃO
  // ============================================
  const formatarDataBR = (data: string) => {
    if (!data) return "";
    if (data.includes("/")) return data;
    if (data.includes("-")) {
      const partes = data.split("-");
      if (partes.length === 3 && partes[0].length === 4) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
    }
    return data;
  };

  const inputStyle: React.CSSProperties = {
    padding: "4px 8px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: "12px",
    fontFamily: theme.fonts.sans,
    outline: "none",
  };

  const labelSmall: React.CSSProperties = {
    fontSize: "10px",
    color: theme.gray[500],
    fontFamily: theme.fonts.sans,
    fontWeight: "600",
    textTransform: "uppercase" as const,
    marginBottom: "2px",
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          flexShrink: 0,
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            fontFamily: theme.fonts.sans,
            margin: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Hospital size={18} style={{ marginRight: "8px" }} /> Dados do
          Transplante
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* SELETOR DE TX */}
          {transplantes.length > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: theme.gray[100],
                borderRadius: theme.borderRadius.md,
                padding: "4px",
              }}
            >
              {transplantes.map((tx, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setTxSelecionado(idx);
                    setEditandoHeader(false);
                  }}
                  style={{
                    padding: "4px 12px",
                    background:
                      txSelecionado === idx
                        ? theme.primary[600]
                        : "transparent",
                    color: txSelecionado === idx ? "white" : theme.gray[600],
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {tx.ordem}º Tx
                </button>
              ))}
            </div>
          )}
          {/* BOTÃO NOVO TX */}
          {!isConvidado && (
            <button
              onClick={adicionarTransplante}
              style={{
                padding: "6px 12px",
                background: "#dc2626",
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Plus size={14} /> Novo Tx (ReTx)
            </button>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* INFO DO TX SELECIONADO (editável) */}
      {/* ============================================ */}
      <div
        style={{
          padding: "8px 12px",
          background:
            transplantes.length > 1
              ? txSelecionado === transplantes.length - 1
                ? "#eff6ff"
                : "#fef3c7"
              : "#f0fdf4",
          borderRadius: theme.borderRadius.md,
          marginBottom: "8px",
          fontSize: "12px",
          fontFamily: theme.fonts.sans,
          border: `1px solid ${
            transplantes.length > 1
              ? txSelecionado === transplantes.length - 1
                ? "#bfdbfe"
                : "#fde68a"
              : "#bbf7d0"
          }`,
        }}
      >
        {editandoHeader &&
        !isConvidado &&
        !(
          txAtual.ordem === 1 &&
          txAtual.dataTransplante &&
          transplantes.length === 1
        ) ? (
          /* ===== MODO EDIÇÃO DO HEADER ===== */
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {/* DATA DO TRANSPLANTE */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={labelSmall}>
                  <Calendar size={10} style={{ marginRight: "2px" }} />
                  Data do {txAtual.ordem}º Transplante
                </span>
                {txAtual.ordem === 1 && txAtual.dataTransplante ? (
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.gray[700],
                      fontFamily: theme.fonts.sans,
                      padding: "4px 0",
                    }}
                  >
                    {formatarDataBR(txAtual.dataTransplante)}
                    <span
                      style={{
                        fontSize: "10px",
                        color: theme.gray[400],
                        fontStyle: "italic",
                        marginLeft: "8px",
                      }}
                    >
                      (editar em Inscrição Tx)
                    </span>
                  </span>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <input
                      id="txDataDia"
                      type="text"
                      placeholder="DD"
                      maxLength={2}
                      value={dataTemp.dia}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                        setDataTemp((prev) => ({ ...prev, dia: v }));
                        if (v.length === 2) {
                          document.getElementById("txDataMes")?.focus();
                        }
                      }}
                      onBlur={() => {
                        if (
                          dataTemp.dia &&
                          dataTemp.mes &&
                          dataTemp.ano.length === 4
                        ) {
                          salvarHeaderTx(
                            "dataTransplante",
                            `${dataTemp.ano}-${dataTemp.mes}-${dataTemp.dia}`
                          );
                        }
                      }}
                      style={{
                        ...inputStyle,
                        width: "36px",
                        textAlign: "center",
                      }}
                    />
                    <span style={{ color: theme.gray[400], fontSize: "12px" }}>
                      /
                    </span>
                    <input
                      id="txDataMes"
                      type="text"
                      placeholder="MM"
                      maxLength={2}
                      value={dataTemp.mes}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                        setDataTemp((prev) => ({ ...prev, mes: v }));
                        if (v.length === 2) {
                          document.getElementById("txDataAno")?.focus();
                        }
                      }}
                      onBlur={() => {
                        if (
                          dataTemp.dia &&
                          dataTemp.mes &&
                          dataTemp.ano.length === 4
                        ) {
                          salvarHeaderTx(
                            "dataTransplante",
                            `${dataTemp.ano}-${dataTemp.mes}-${dataTemp.dia}`
                          );
                        }
                      }}
                      style={{
                        ...inputStyle,
                        width: "36px",
                        textAlign: "center",
                      }}
                    />
                    <span style={{ color: theme.gray[400], fontSize: "12px" }}>
                      /
                    </span>
                    <input
                      id="txDataAno"
                      type="text"
                      placeholder="AAAA"
                      maxLength={4}
                      value={dataTemp.ano}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setDataTemp((prev) => ({ ...prev, ano: v }));
                      }}
                      onBlur={() => {
                        if (
                          dataTemp.dia &&
                          dataTemp.mes &&
                          dataTemp.ano.length === 4
                        ) {
                          salvarHeaderTx(
                            "dataTransplante",
                            `${dataTemp.ano}-${dataTemp.mes}-${dataTemp.dia}`
                          );
                        }
                      }}
                      style={{
                        ...inputStyle,
                        width: "52px",
                        textAlign: "center",
                      }}
                    />
                  </div>
                )}
              </div>
              {/* MOTIVO DO RETX (só a partir do 2º) */}
              {txAtual.ordem > 1 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: "200px",
                  }}
                >
                  <span style={labelSmall}>
                    <AlertTriangle size={10} style={{ marginRight: "2px" }} />
                    Motivo do Retransplante
                  </span>
                  <select
                    value={txAtual.motivoRetx || ""}
                    onChange={(e) =>
                      salvarHeaderTx("motivoRetx", e.target.value)
                    }
                    style={{ ...inputStyle }}
                  >
                    <option value="">Selecione o motivo...</option>
                    <option value="Disfunção primária do enxerto">
                      Disfunção primária do enxerto (PNF)
                    </option>
                    <option value="Trombose da artéria hepática">
                      Trombose da artéria hepática
                    </option>
                    <option value="Trombose da veia porta">
                      Trombose da veia porta
                    </option>
                    <option value="Rejeição crônica">
                      Rejeição crônica (ductopênica)
                    </option>
                    <option value="Recidiva da doença de base">
                      Recidiva da doença de base
                    </option>
                    <option value="Complicação biliar irreversível">
                      Complicação biliar irreversível
                    </option>
                    <option value="Recidiva de CHC">Recidiva de CHC</option>
                    <option value="Recidiva de hepatite C">
                      Recidiva de hepatite C
                    </option>
                    <option value="Estenose/trombose vascular tardia">
                      Estenose/trombose vascular tardia
                    </option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              )}
              {/* BOTÕES */}
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  onClick={() => {
                    if (
                      dataTemp.dia &&
                      dataTemp.mes &&
                      dataTemp.ano.length === 4
                    ) {
                      salvarHeaderTx(
                        "dataTransplante",
                        `${dataTemp.ano}-${dataTemp.mes}-${dataTemp.dia}`
                      );
                    }
                    setEditandoHeader(false);
                  }}
                  style={{
                    padding: "4px 12px",
                    background: theme.primary[600],
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "11px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  OK
                </button>
                {/* REMOVER TX (só se vazio e não é o 1º) */}
                {transplantes.length > 1 && txSelecionado > 0 && (
                  <button
                    onClick={removerTransplante}
                    title="Remover transplante (somente se vazio)"
                    style={{
                      padding: "4px 8px",
                      background: "#fee2e2",
                      color: "#dc2626",
                      border: "1px solid #fecaca",
                      borderRadius: theme.borderRadius.sm,
                      fontSize: "11px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Trash2 size={12} /> Remover
                  </button>
                )}
              </div>
            </div>
            {/* EXIBIR MOTIVO ATUAL SE JÁ PREENCHIDO */}
            {txAtual.motivoRetx && txAtual.ordem > 1 && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#92400e",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertTriangle size={11} /> Motivo: {txAtual.motivoRetx}
              </div>
            )}
          </div>
        ) : (
          /* ===== MODO VISUALIZAÇÃO DO HEADER ===== */
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: isConvidado ? "default" : "pointer",
            }}
            onClick={() => {
              if (!isConvidado) {
                if (txAtual.ordem === 1 && txAtual.dataTransplante) return;
                if (!headerDentroJanela24h) return;
                const partes = (txAtual.dataTransplante || "").split("-");
                setDataTemp({
                  ano: partes[0] || "",
                  mes: partes[1] || "",
                  dia: partes[2] || "",
                });
                setEditandoHeader(true);
              }
            }}
            title={
              isConvidado
                ? ""
                : "Clique para editar data e motivo do transplante"
            }
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontWeight: "600",
                  color:
                    transplantes.length > 1
                      ? txSelecionado === transplantes.length - 1
                        ? theme.primary[700]
                        : "#92400e"
                      : "#166534",
                }}
              >
                {txAtual.ordem}º Transplante
                {txAtual.dataTransplante
                  ? ` — ${formatarDataBR(txAtual.dataTransplante)}`
                  : " — Data não informada"}
              </span>
              {txAtual.motivoRetx && txAtual.ordem > 1 && (
                <span
                  style={{
                    background: "#fed7aa",
                    color: "#9a3412",
                    padding: "1px 8px",
                    borderRadius: "10px",
                    fontSize: "10px",
                    fontWeight: "600",
                  }}
                >
                  {txAtual.motivoRetx}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {txSelecionado < transplantes.length - 1 && (
                <span
                  style={{
                    background: "#fbbf24",
                    color: "#78350f",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "600",
                  }}
                >
                  Histórico
                </span>
              )}
              {!isConvidado &&
                !(txAtual.ordem === 1 && txAtual.dataTransplante) && (
                  <span
                    style={{
                      color: headerDentroJanela24h
                        ? theme.gray[400]
                        : "#dc2626",
                      fontSize: "10px",
                      fontStyle: "italic",
                    }}
                  >
                    {headerDentroJanela24h
                      ? "clique para editar"
                      : "edição bloqueada (>24h)"}
                  </span>
                )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SUB-ABAS */}
      {/* ============================================ */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          marginBottom: "8px",
          borderBottom: `1px solid ${theme.gray[200]}`,
          flexShrink: 0,
        }}
      >
        {subAbas.map((aba) => (
          <button
            key={aba.id}
            onClick={() => setSubAba(aba.id as any)}
            style={{
              padding: "6px 12px",
              background:
                subAba === aba.id ? theme.primary[600] : "transparent",
              color: subAba === aba.id ? "white" : theme.gray[600],
              border: "none",
              borderRadius: `${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0`,
              fontSize: "12px",
              fontWeight: "600",
              fontFamily: theme.fonts.sans,
              cursor: "pointer",
              transition: theme.transitions.normal,
            }}
            onMouseOver={(e) => {
              if (subAba !== aba.id)
                e.currentTarget.style.background = theme.gray[100];
            }}
            onMouseOut={(e) => {
              if (subAba !== aba.id)
                e.currentTarget.style.background = "transparent";
            }}
          >
            {aba.icon && <aba.icon size={12} style={{ marginRight: "4px" }} />}
            {aba.label}
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* CONTEÚDO */}
      {/* ============================================ */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "8px",
          paddingBottom: "40px",
          maxHeight: "calc(100vh - 320px)",
        }}
      >
        {subAba === "equipe" && (
          <EquipeCirurgica
            paciente={pacienteVirtual}
            onSalvarTx={(d) => salvarDadosTx("equipeCirurgica", d)}
          />
        )}
        {subAba === "dados" && (
          <DadosComplementares
            paciente={pacienteVirtual}
            onSalvarTx={(d) => salvarDadosTx("dadosComplementares", d)}
          />
        )}
        {subAba === "tecnica" && (
          <TecnicaOperatoria
            paciente={pacienteVirtual}
            onSalvarTx={(d) => salvarDadosTx("tecnicaOperatoria", d)}
          />
        )}
        {subAba === "pos" && (
          <PosOperatorio
            paciente={pacienteVirtual}
            onSalvarTx={(d) => salvarDadosTx("posOperatorio", d)}
          />
        )}
        {subAba === "timeline" && (
          <div style={{ padding: "8px 0" }}>
            {/* LINHA DO TEMPO VISUAL */}
            <div
              style={{
                position: "relative",
                padding: "0 0 0 32px",
                marginBottom: "24px",
              }}
            >
              {/* Linha vertical */}
              <div
                style={{
                  position: "absolute",
                  left: "11px",
                  top: "8px",
                  bottom: "8px",
                  width: "2px",
                  background: `linear-gradient(to bottom, ${theme.primary[400]}, ${theme.gray[300]})`,
                }}
              />
              {transplantes.map((tx, idx) => {
                const temEquipe = verificarPreenchimento(tx.equipeCirurgica);
                const temDados = verificarPreenchimento(tx.dadosComplementares);
                const temTecnica = verificarPreenchimento(tx.tecnicaOperatoria);
                const temPos = verificarPreenchimento(tx.posOperatorio);
                const totalPreenchido = [
                  temEquipe,
                  temDados,
                  temTecnica,
                  temPos,
                ].filter(Boolean).length;
                const isUltimo = idx === transplantes.length - 1;

                return (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      marginBottom:
                        idx < transplantes.length - 1 ? "24px" : "0",
                    }}
                  >
                    {/* Bolinha na timeline */}
                    <div
                      style={{
                        position: "absolute",
                        left: "-28px",
                        top: "8px",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        background: isUltimo ? theme.primary[600] : "#f59e0b",
                        border: `3px solid ${
                          isUltimo ? theme.primary[200] : "#fde68a"
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "8px",
                          color: "white",
                          fontWeight: "800",
                        }}
                      >
                        {tx.ordem}
                      </span>
                    </div>
                    {/* CARD DO TRANSPLANTE */}
                    <div
                      onClick={() => {
                        setTxSelecionado(idx);
                        setSubAba("equipe");
                      }}
                      style={{
                        background: isUltimo ? "#eff6ff" : "#fffbeb",
                        border: `1px solid ${isUltimo ? "#bfdbfe" : "#fde68a"}`,
                        borderRadius: theme.borderRadius.lg,
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.boxShadow = theme.shadows.md;
                        e.currentTarget.style.transform = "translateX(4px)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                    >
                      {/* Header do card */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: isUltimo ? theme.primary[800] : "#92400e",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {tx.ordem}º Transplante
                          </span>
                          {tx.motivoRetx && tx.ordem > 1 && (
                            <span
                              style={{
                                background: "#fed7aa",
                                color: "#9a3412",
                                padding: "1px 8px",
                                borderRadius: "10px",
                                fontSize: "10px",
                                fontWeight: "600",
                              }}
                            >
                              {tx.motivoRetx}
                            </span>
                          )}
                          {!isUltimo && (
                            <span
                              style={{
                                background: "#fbbf24",
                                color: "#78350f",
                                padding: "1px 8px",
                                borderRadius: "10px",
                                fontSize: "10px",
                                fontWeight: "600",
                              }}
                            >
                              Histórico
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            color: isUltimo ? theme.primary[600] : "#b45309",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {tx.dataTransplante
                            ? formatarDataBR(tx.dataTransplante)
                            : "Sem data"}
                        </span>
                      </div>
                      {/* Barra de progresso */}
                      <div
                        style={{
                          display: "flex",
                          gap: "3px",
                          marginBottom: "8px",
                        }}
                      >
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            style={{
                              flex: 1,
                              height: "4px",
                              borderRadius: "2px",
                              background:
                                i <= totalPreenchido
                                  ? isUltimo
                                    ? theme.primary[500]
                                    : "#f59e0b"
                                  : theme.gray[200],
                            }}
                          />
                        ))}
                      </div>
                      {/* Status das seções */}
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {[
                          { label: "Equipe", preenchido: temEquipe },
                          { label: "Dados Compl.", preenchido: temDados },
                          { label: "Técnica Op.", preenchido: temTecnica },
                          { label: "Pós-Op.", preenchido: temPos },
                        ].map((item) => (
                          <div
                            key={item.label}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "11px",
                              fontFamily: theme.fonts.sans,
                              color: item.preenchido
                                ? "#166534"
                                : theme.gray[400],
                              fontWeight: item.preenchido ? "600" : "400",
                            }}
                          >
                            {item.preenchido ? (
                              <CheckCircle2 size={12} color="#16a34a" />
                            ) : (
                              <Circle size={12} color={theme.gray[300]} />
                            )}
                            {item.label}
                          </div>
                        ))}
                      </div>
                      {/* Tempo desde o Tx */}
                      {tx.dataTransplante && (
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "10px",
                            color: theme.gray[500],
                            fontFamily: theme.fonts.sans,
                            fontStyle: "italic",
                          }}
                        >
                          {(() => {
                            const dtTx = new Date(
                              tx.dataTransplante + "T00:00:00"
                            );
                            const hoje = new Date();
                            const diffMs = hoje.getTime() - dtTx.getTime();
                            const dias = Math.floor(
                              diffMs / (1000 * 60 * 60 * 24)
                            );
                            if (dias < 30) return `Há ${dias} dias`;
                            const meses = Math.floor(dias / 30);
                            if (meses < 12)
                              return `Há ${meses} ${
                                meses === 1 ? "mês" : "meses"
                              }`;
                            const anos = Math.floor(meses / 12);
                            const mesesRest = meses % 12;
                            return `Há ${anos} ${anos === 1 ? "ano" : "anos"}${
                              mesesRest > 0
                                ? ` e ${mesesRest} ${
                                    mesesRest === 1 ? "mês" : "meses"
                                  }`
                                : ""
                            }`;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* LEGENDA */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                padding: "12px",
                background: theme.gray[50],
                borderRadius: theme.borderRadius.md,
                fontSize: "11px",
                fontFamily: theme.fonts.sans,
                color: theme.gray[500],
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: theme.primary[600],
                  }}
                />
                Transplante atual
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: "#f59e0b",
                  }}
                />
                Transplante anterior
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <CheckCircle2 size={12} color="#16a34a" />
                Seção preenchida
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Circle size={12} color={theme.gray[300]} />
                Seção pendente
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
